import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import Logo from "./Logo";
import {
  login,
  requestPasswordResetCode,
  requestSignupCode,
  resetPassword,
  signup,
  verifyPasswordResetCode,
  verifySignupCode
} from "../api";

const AuthScreen = ({ onLogin, onBack }) => {
  const [mode, setMode] = useState("signin");
  const [roleTab, setRoleTab] = useState("lender");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [pendingSignupPayload, setPendingSignupPayload] = useState(null);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState(Array(6).fill(""));
  const [verificationError, setVerificationError] = useState("");
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetStep, setResetStep] = useState("request");
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState(Array(6).fill(""));
  const [resetNewPassword, setResetNewPassword] = useState("");
  const [resetConfirmPassword, setResetConfirmPassword] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetSuccess, setResetSuccess] = useState("");
  const [isResetting, setIsResetting] = useState(false);
  const [isResetResending, setIsResetResending] = useState(false);
  const [error, setError] = useState("");
  const isSignUp = mode === "signup";
  const codeInputs = useRef([]);
  const resetCodeInputs = useRef([]);

  const resetVerificationState = () => {
    setShowVerification(false);
    setVerificationCode(Array(6).fill(""));
    setVerificationError("");
    setPendingSignupPayload(null);
    setIsVerifying(false);
    setIsResending(false);
  };

  const resetPasswordState = () => {
    setShowPasswordReset(false);
    setResetStep("request");
    setResetEmail("");
    setResetCode(Array(6).fill(""));
    setResetNewPassword("");
    setResetConfirmPassword("");
    setResetError("");
    setResetSuccess("");
    setIsResetting(false);
    setIsResetResending(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent double-submit
    if (isLoading) {
      return;
    }

    if (!email.trim() || !password) {
      setError("Enter both an email and password.");
      return;
    }

    if (isSignUp && password !== confirmPassword) {
      setError("Passwords must match.");
      return;
    }

    if (isSignUp) {
      if (!firstName.trim() || !lastName.trim()) {
        setError("Please complete first name and last name.");
        return;
      }
      if (roleTab === "lender" && !company.trim()) {
        setError("Please complete first name, last name, and company.");
        return;
      }
    }

    setError("");
    setIsLoading(true);

    const roleValue = roleTab === "lender" ? "Lender" : "Borrower";
    const payload = isSignUp
      ? {
        email: email.trim(),
        password,
        username: email.trim(),
        membership: "Free",
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        company: roleTab === "lender" ? company.trim() : undefined,
        role: roleValue
      }
      : { email: email.trim(), password }; // No role for login - server determines it

    if (isSignUp) {
      try {
        await requestSignupCode({ email: payload.email });
        setPendingSignupPayload(payload);
        setVerificationCode(Array(6).fill(""));
        setVerificationError("");
        setShowVerification(true);
      } catch (authError) {
        console.error(authError);
        setError(authError.message || "Unable to send verification code. Please try again.");
      } finally {
        setIsLoading(false);
      }
      return;
    }

    try {
      const response = await login(payload);
      if (response?.status !== undefined && (response.status < 200 || response.status >= 300)) {
        throw new Error("Auth failed");
      }
      const userEmail = response?.data?.user?.email || email.trim();

      // IMPORTANT: Always use server role, NEVER client selection
      // Server role is authoritative to prevent role spoofing
      const serverRole = response?.data?.user?.role || response?.data?.role || roleValue;

      console.log("[AuthScreen] Login/Signup success:", {
        email: userEmail,
        role: serverRole,
        response: response?.data
      });

      await onLogin({ email: userEmail, role: serverRole });
    } catch (authError) {
      console.error(authError);
      setError("Invalid email or password.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (event) => {
    event.preventDefault();
    if (!pendingSignupPayload) {
      setVerificationError("Please restart signup to request a new code.");
      return;
    }

    const codeValue = verificationCode.join("");
    if (codeValue.length !== 6) {
      setVerificationError("Enter the 6-digit verification code.");
      return;
    }

    setVerificationError("");
    setIsVerifying(true);

    try {
      await verifySignupCode({ email: pendingSignupPayload.email, code: codeValue });
      const response = await signup(pendingSignupPayload);
      if (response?.status !== undefined && (response.status < 200 || response.status >= 300)) {
        throw new Error("Auth failed");
      }
      const userEmail = response?.data?.user?.email || pendingSignupPayload.email;
      const serverRole =
        response?.data?.user?.role || response?.data?.role || pendingSignupPayload.role;

      console.log("[AuthScreen] Login/Signup success:", {
        email: userEmail,
        role: serverRole,
        response: response?.data
      });

      resetVerificationState();
      await onLogin({ email: userEmail, role: serverRole });
    } catch (authError) {
      console.error(authError);
      if (authError.message === "USER_EXISTS") {
        resetVerificationState();
        setError("This email is already registered. Please sign in instead.");
        setTimeout(() => switchMode("signin"), 2000);
      } else {
        setVerificationError(authError.message || "Verification failed. Please try again.");
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (!pendingSignupPayload) {
      setVerificationError("Please restart signup to request a new code.");
      return;
    }

    setIsResending(true);
    setVerificationError("");
    try {
      await requestSignupCode({ email: pendingSignupPayload.email });
    } catch (authError) {
      console.error(authError);
      setVerificationError(authError.message || "Unable to resend code. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const handleCodeChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...verificationCode];
    next[index] = digit;
    setVerificationCode(next);
    setVerificationError("");

    if (digit && codeInputs.current[index + 1]) {
      codeInputs.current[index + 1].focus();
    }
  };

  const handleCodeKeyDown = (index, event) => {
    if (event.key === "Backspace" && !verificationCode[index] && codeInputs.current[index - 1]) {
      codeInputs.current[index - 1].focus();
    }
    if (event.key === "ArrowLeft" && codeInputs.current[index - 1]) {
      codeInputs.current[index - 1].focus();
    }
    if (event.key === "ArrowRight" && codeInputs.current[index + 1]) {
      codeInputs.current[index + 1].focus();
    }
  };

  const handleCodePaste = (event) => {
    const paste = event.clipboardData.getData("text");
    const digits = paste.replace(/\D/g, "").slice(0, 6).split("");
    if (!digits.length) {
      return;
    }
    const next = Array(6).fill("");
    digits.forEach((digit, index) => {
      next[index] = digit;
    });
    setVerificationCode(next);
    setVerificationError("");
    const focusIndex = Math.min(digits.length, 6) - 1;
    if (codeInputs.current[focusIndex]) {
      codeInputs.current[focusIndex].focus();
    }
  };

  const openPasswordReset = () => {
    setResetEmail(email.trim());
    setResetStep("request");
    setResetCode(Array(6).fill(""));
    setResetNewPassword("");
    setResetConfirmPassword("");
    setResetError("");
    setResetSuccess("");
    setShowPasswordReset(true);
  };

  const handleRequestPasswordReset = async (event) => {
    event.preventDefault();
    const trimmedEmail = resetEmail.trim();
    if (!trimmedEmail) {
      setResetError("Enter your email to receive a reset code.");
      return;
    }

    setResetError("");
    setIsResetting(true);
    try {
      await requestPasswordResetCode({ email: trimmedEmail });
      setResetStep("verify");
    } catch (resetError) {
      console.error(resetError);
      setResetError(resetError.message || "Unable to send reset code.");
    } finally {
      setIsResetting(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    const trimmedEmail = resetEmail.trim();
    const codeValue = resetCode.join("");

    if (!trimmedEmail) {
      setResetError("Enter your email to continue.");
      return;
    }
    if (codeValue.length !== 6) {
      setResetError("Enter the 6-digit reset code.");
      return;
    }
    if (!resetNewPassword) {
      setResetError("Enter a new password.");
      return;
    }
    if (resetNewPassword !== resetConfirmPassword) {
      setResetError("Passwords must match.");
      return;
    }

    setResetError("");
    setIsResetting(true);
    try {
      await verifyPasswordResetCode({ email: trimmedEmail, code: codeValue });
      await resetPassword({ email: trimmedEmail, code: codeValue, newPassword: resetNewPassword });
      setResetSuccess("Password updated. Please sign in.");
      setTimeout(() => {
        resetPasswordState();
        setMode("signin");
        setEmail(trimmedEmail);
        setPassword("");
        setConfirmPassword("");
      }, 1200);
    } catch (resetError) {
      console.error(resetError);
      setResetError(resetError.message || "Unable to reset password.");
    } finally {
      setIsResetting(false);
    }
  };

  const handleResetResendCode = async () => {
    const trimmedEmail = resetEmail.trim();
    if (!trimmedEmail) {
      setResetError("Enter your email to resend the code.");
      return;
    }

    setIsResetResending(true);
    setResetError("");
    try {
      await requestPasswordResetCode({ email: trimmedEmail });
    } catch (resetError) {
      console.error(resetError);
      setResetError(resetError.message || "Unable to resend code.");
    } finally {
      setIsResetResending(false);
    }
  };

  const handleResetCodeChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...resetCode];
    next[index] = digit;
    setResetCode(next);
    setResetError("");

    if (digit && resetCodeInputs.current[index + 1]) {
      resetCodeInputs.current[index + 1].focus();
    }
  };

  const handleResetCodeKeyDown = (index, event) => {
    if (event.key === "Backspace" && !resetCode[index] && resetCodeInputs.current[index - 1]) {
      resetCodeInputs.current[index - 1].focus();
    }
    if (event.key === "ArrowLeft" && resetCodeInputs.current[index - 1]) {
      resetCodeInputs.current[index - 1].focus();
    }
    if (event.key === "ArrowRight" && resetCodeInputs.current[index + 1]) {
      resetCodeInputs.current[index + 1].focus();
    }
  };

  const handleResetCodePaste = (event) => {
    const paste = event.clipboardData.getData("text");
    const digits = paste.replace(/\D/g, "").slice(0, 6).split("");
    if (!digits.length) {
      return;
    }
    const next = Array(6).fill("");
    digits.forEach((digit, index) => {
      next[index] = digit;
    });
    setResetCode(next);
    setResetError("");
    const focusIndex = Math.min(digits.length, 6) - 1;
    if (resetCodeInputs.current[focusIndex]) {
      resetCodeInputs.current[focusIndex].focus();
    }
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setError("");
    setIsLoading(false);
    resetVerificationState();
    resetPasswordState();
    setConfirmPassword("");
    setPassword("");
    setFirstName("");
    setLastName("");
    setCompany("");
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onBack();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onBack]);

  return (
    <div
      className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4 font-sans text-slate-100"
      onClick={onBack}
      role="presentation"
    >
      <div className="w-full max-w-md space-y-8" onClick={(event) => event.stopPropagation()}>
        <div className="flex flex-col items-center space-y-4">
          <div onClick={onBack} className="cursor-pointer hover:opacity-80 transition-opacity">
            <Logo className="h-[3.2rem] w-auto" />
          </div>
          <h2 className="text-3xl font-bold tracking-tight text-center">
            {isSignUp ? "Create your account" : "Sign in to your account"}
          </h2>
        </div>

        <div className="bg-slate-900 p-8 rounded-2xl border border-slate-800 shadow-2xl">
          {isSignUp && (
            <div className="flex items-center gap-2 mb-6">
              <button
                type="button"
                onClick={() => setRoleTab("lender")}
                className={`flex-1 py-2 rounded-lg font-semibold text-sm border ${roleTab === "lender"
                  ? "bg-yellow-600 border-yellow-500 text-white"
                  : "bg-slate-950 border-slate-800 text-slate-300 hover:bg-slate-800"
                  }`}
              >
                Lender
              </button>
              <button
                type="button"
                onClick={() => setRoleTab("borrower")}
                className={`flex-1 py-2 rounded-lg font-semibold text-sm border ${roleTab === "borrower"
                  ? "bg-yellow-600 border-yellow-500 text-white"
                  : "bg-slate-950 border-slate-800 text-slate-300 hover:bg-slate-800"
                  }`}
              >
                Borrower
              </button>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-slate-950 border border-slate-700 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition-all text-white placeholder-slate-500"
                placeholder="you@company.com"
                autoComplete="email"
                autoFocus
              />
            </div>

            {isSignUp && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">First Name</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-950 border border-slate-700 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition-all text-white placeholder-slate-500"
                    placeholder="First name"
                    autoComplete="given-name"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">Last Name</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-950 border border-slate-700 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition-all text-white placeholder-slate-500"
                    placeholder="Last name"
                    autoComplete="family-name"
                  />
                </div>
              </div>
            )}

            {isSignUp && roleTab === "lender" && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">Company</label>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-950 border border-slate-700 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition-all text-white placeholder-slate-500"
                  placeholder="Company name"
                  autoComplete="organization"
                />
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-slate-950 border border-slate-700 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition-all text-white placeholder-slate-500"
                placeholder="Enter your password"
                autoComplete={isSignUp ? "new-password" : "current-password"}
              />
            </div>

            {isSignUp && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">Confirm password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-950 border border-slate-700 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition-all text-white placeholder-slate-500"
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                />
              </div>
            )}

            {!isSignUp && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={openPasswordReset}
                  className="text-xs font-semibold text-yellow-400 hover:text-yellow-300 transition-colors"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {error && (
              <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/40 rounded-lg px-4 py-3">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg font-bold text-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <span>{isSignUp ? "Create account" : "Sign In"}</span>
              )}
            </button>

            <div className="text-center text-sm text-slate-400">
              {isSignUp ? (
                <div className="space-y-1">
                  <p>Already registered?</p>
                  <button
                    type="button"
                    onClick={() => switchMode("signin")}
                    className="font-semibold text-yellow-400 hover:text-yellow-300 transition-colors"
                  >
                    Back to sign in
                  </button>
                </div>
              ) : (
                <div className="space-y-1">
                  <p>Don&apos;t have an account?</p>
                  <button
                    type="button"
                    onClick={() => switchMode("signup")}
                    className="font-semibold text-yellow-400 hover:text-yellow-300 transition-colors"
                  >
                    Create one in seconds
                  </button>
                </div>
              )}
            </div>

            <div className="text-center text-xs text-slate-500 space-x-3">
              <a href="/terms" className="hover:text-yellow-300 transition-colors">
                Terms of Use
              </a>
              <span className="text-slate-600">•</span>
              <a href="/privacy" className="hover:text-yellow-300 transition-colors">
                Privacy Policy
              </a>
            </div>
          </form>
        </div>
      </div>

      {showVerification && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Email verification"
          onClick={resetVerificationState}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="space-y-2 text-center">
              <h3 className="text-xl font-semibold text-white">Verify your email</h3>
              <p className="text-sm text-slate-400">
                We sent a 6-digit code to {pendingSignupPayload?.email}. Enter it to complete signup. Codes expire in 10 minutes.
              </p>
            </div>

            <form onSubmit={handleVerifyCode} className="mt-6 space-y-5">
              <div className="flex justify-between gap-2" onPaste={handleCodePaste}>
                {verificationCode.map((digit, index) => (
                  <input
                    key={`signup-code-${index}`}
                    ref={(input) => {
                      codeInputs.current[index] = input;
                    }}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={digit}
                    onChange={(event) => handleCodeChange(index, event.target.value)}
                    onKeyDown={(event) => handleCodeKeyDown(index, event)}
                    className="h-14 w-12 rounded-lg border border-slate-700 bg-slate-950 text-center text-2xl font-bold text-white outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-yellow-500"
                    autoFocus={index === 0}
                  />
                ))}
              </div>

              {verificationError && (
                <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/40 rounded-lg px-4 py-3">
                  {verificationError}
                </div>
              )}

              <button
                type="submit"
                disabled={isVerifying}
                className="w-full py-3 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg font-bold text-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isVerifying ? <Loader2 className="w-5 h-5 animate-spin" /> : <span>Verify &amp; create account</span>}
              </button>

              <div className="flex items-center justify-between text-sm text-slate-400">
                <button
                  type="button"
                  onClick={handleResendCode}
                  className="font-semibold text-yellow-400 hover:text-yellow-300 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={isResending}
                >
                  {isResending ? "Sending..." : "Resend code"}
                </button>
                <button
                  type="button"
                  onClick={resetVerificationState}
                  className="font-semibold text-slate-400 hover:text-slate-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPasswordReset && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Password reset"
          onClick={resetPasswordState}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="space-y-2 text-center">
              <h3 className="text-xl font-semibold text-white">Reset your password</h3>
              <p className="text-sm text-slate-400">
                {resetStep === "request"
                  ? "Enter your email to receive a 6-digit reset code."
                  : "Enter the 6-digit code and choose a new password. Codes expire in 10 minutes."}
              </p>
            </div>

            {resetStep === "request" ? (
              <form onSubmit={handleRequestPasswordReset} className="mt-6 space-y-5">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">Email</label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(event) => setResetEmail(event.target.value)}
                    className="w-full px-4 py-3 bg-slate-950 border border-slate-700 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition-all text-white placeholder-slate-500"
                    placeholder="you@company.com"
                    autoComplete="email"
                  />
                </div>

                {resetError && (
                  <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/40 rounded-lg px-4 py-3">
                    {resetError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isResetting}
                  className="w-full py-3 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg font-bold text-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isResetting ? <Loader2 className="w-5 h-5 animate-spin" /> : <span>Send reset code</span>}
                </button>

                <div className="flex items-center justify-end text-sm text-slate-400">
                  <button
                    type="button"
                    onClick={resetPasswordState}
                    className="font-semibold text-slate-400 hover:text-slate-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="mt-6 space-y-5">
                <div className="flex justify-between gap-2" onPaste={handleResetCodePaste}>
                  {resetCode.map((digit, index) => (
                    <input
                      key={`reset-code-${index}`}
                      ref={(input) => {
                        resetCodeInputs.current[index] = input;
                      }}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      value={digit}
                      onChange={(event) => handleResetCodeChange(index, event.target.value)}
                      onKeyDown={(event) => handleResetCodeKeyDown(index, event)}
                      className="h-14 w-12 rounded-lg border border-slate-700 bg-slate-950 text-center text-2xl font-bold text-white outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-yellow-500"
                      autoFocus={index === 0}
                    />
                  ))}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">New password</label>
                  <input
                    type="password"
                    value={resetNewPassword}
                    onChange={(event) => setResetNewPassword(event.target.value)}
                    className="w-full px-4 py-3 bg-slate-950 border border-slate-700 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition-all text-white placeholder-slate-500"
                    placeholder="Enter a new password"
                    autoComplete="new-password"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">Confirm password</label>
                  <input
                    type="password"
                    value={resetConfirmPassword}
                    onChange={(event) => setResetConfirmPassword(event.target.value)}
                    className="w-full px-4 py-3 bg-slate-950 border border-slate-700 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition-all text-white placeholder-slate-500"
                    placeholder="Re-enter your new password"
                    autoComplete="new-password"
                  />
                </div>

                {resetError && (
                  <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/40 rounded-lg px-4 py-3">
                    {resetError}
                  </div>
                )}

                {resetSuccess && (
                  <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/40 rounded-lg px-4 py-3">
                    {resetSuccess}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isResetting}
                  className="w-full py-3 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg font-bold text-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isResetting ? <Loader2 className="w-5 h-5 animate-spin" /> : <span>Reset password</span>}
                </button>

                <div className="flex items-center justify-between text-sm text-slate-400">
                  <button
                    type="button"
                    onClick={handleResetResendCode}
                    className="font-semibold text-yellow-400 hover:text-yellow-300 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={isResetResending}
                  >
                    {isResetResending ? "Sending..." : "Resend code"}
                  </button>
                  <button
                    type="button"
                    onClick={resetPasswordState}
                    className="font-semibold text-slate-400 hover:text-slate-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

AuthScreen.propTypes = {
  onLogin: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default AuthScreen;
